import { Component } from 'react'
import { bool, func, object } from 'prop-types'
import { connect } from 'react-redux'
import { noop, omit } from 'lodash-es'
import Immutable from 'immutable'
import cc from 'classcat'

import NestedList, { NestedListItem } from '../../components/NestedList'
import translate, { createT } from '../../utils/translate'
import { i18nInstancesContext } from '../I18nProvider'
import { mapDeep } from '../../utils/immutableTreeHelpers'
import { maxNestedPageLevel } from './Panels/SelectPageLevel/PageOption'
import { updateNavigation as storeUpdateNavigation } from '../../store/actions'
import Link from '../../components/templateComponents/Link'
import PageModal from './PageModal'
import VideoTutorialButton from '../videoTutorials/VideoTutorialButton'
import compose from '../../utils/compose'
import flatMap from '../../utils/nestedListUtils/flatMap'
import inheritVisibility from '../../utils/inheritVisibility'
import withI18n from '../../components/withI18n'

function getNavigationNestingDepth(nodes) {
  return flatMap(nodes, (node) => node.get('__level')).reduce((max, level) => Math.max(max, level), 0)
}

function getContentTypeCategoryClassName(id, href, type) {
  if (id === 'footer') return 'footer'

  return href === '/' ? 'start' : type
}

class PageManager extends Component {
  static propTypes = {
    isBeyondShop: bool.isRequired,
    isTutorialVideoAvailable: bool.isRequired,
    navigation: object.isRequired,
    legalPages: object,
    footerPages: object.isRequired,
    location: object.isRequired,
    updateNavigation: func.isRequired,
    i18n: object,
    t: func,
    tReady: bool,
    disabled: bool,
  }

  state = {
    addPageModal: false,
    activePageSettingsModal: null,
    duplicatePageModal: null,
  }

  tScoped = (subKey, ...args) => {
    return this.props.t(`components.editorSidebarComponent.pageManagerSection.${subKey}`, ...args)
  }

  constructor(props, context) {
    super(props)

    // Needed for legal page title translation in Beyond shops
    this.shopT = createT(context.shopI18n, true)
  }

  componentDidMount() {
    // Needed for content page deep linking
    if (window.location.hash === '#addContentPage') {
      this.setState({ addPageModal: true })
    }
  }

  get sfLocationPath() {
    // construct the actual storefront path from the current editor url to check the currently active page.
    // We also have to remove any language from the path since the page we're comparing against misses that
    // If we're on the home page (/editor) we'd end up with an empty string which then falls back to `/`
    return this.props.location.pathname.replace(/^\/editor(\/\w{2})?/, '') || '/'
  }

  validateNavigation = (newNavigation, oldNavigation) => {
    if (newNavigation === oldNavigation) return true

    if (this.props.isBeyondShop) {
      const firstNavigationEntry = newNavigation.get(0)

      if (firstNavigationEntry.get('slug') !== 'home') {
        return this.tScoped('homepageFirstErrorMessage')
      }

      if (firstNavigationEntry.get('children').count() > 0) {
        return this.tScoped('homepageChildrenErrorMessage')
      }
    }

    if (getNavigationNestingDepth(newNavigation) > 3) {
      return this.tScoped('maxNestingErrorMessage', { maxLevels: maxNestedPageLevel })
    }

    return true
  }

  updateNavigation = (newNavigation) => {
    this.props.updateNavigation(inheritVisibility(newNavigation))
  }

  render() {
    const { navigation, isBeyondShop, isTutorialVideoAvailable, footerPages = Immutable.List(), disabled } = this.props
    const { addPageModal, activePageSettingsModal, duplicatePageModal } = this.state
    const { tScoped } = this

    const htmlAttributes = omit(this.props, [
      'isBeyondShop',
      'isTutorialVideoAvailable',
      'navigation',
      'footerPages',
      'legalPages',
      'updateNavigation',
      't',
    ])

    return (
      <div {...htmlAttributes}>
        <div className="ep-scrollable">
          {!isBeyondShop && !disabled ? (
            <h4 key="cat" className="ep-page-manager-list-heading">
              {tScoped('categoriesSection.title')}
            </h4>
          ) : null}
          {!isBeyondShop && !disabled ? this.renderCategories() : null}

          {!disabled ? <h4 className="ep-page-manager-list-heading">{tScoped('contentPagesSection.title')}</h4> : null}
          {!disabled ? this.renderEditorialPages() : null}
          {!disabled ? this.renderFooterPages() : null}
          <div className="ep-sidebar-module-page-manager-button">
            <button
              disabled={disabled}
              className="ep-add-page-button"
              onClick={() => this.setState({ addPageModal: true })}
            >
              {tScoped('contentPagesSection.createPageButton.title')}
            </button>
          </div>
          {!isBeyondShop && isTutorialVideoAvailable && (
            <div className="ep-page-manager-list-video-tutorial-wrapper">
              <VideoTutorialButton tutorial="pages">
                {tScoped('videoTutorial.videoTutorialButton.label')}
              </VideoTutorialButton>
            </div>
          )}
          {!disabled ? (
            <h4 className="ep-page-manager-list-heading">
              {tScoped('legalPagesSection.title')}
              {!isBeyondShop && isTutorialVideoAvailable && (
                <span className="ep-legalpages-video-tutorial">
                  <VideoTutorialButton tutorial="legalpages">
                    <span className="sr-only">
                      {tScoped('legalPagesSection.videoTutorial.videoTutorialButton.label')}
                    </span>
                  </VideoTutorialButton>
                </span>
              )}
            </h4>
          ) : null}
          {!disabled ? this.renderLegalPages() : null}
        </div>
        {activePageSettingsModal && (
          <PageModal
            mode="edit"
            page={activePageSettingsModal}
            isActivePage={this.sfLocationPath === activePageSettingsModal.get('href')}
            mainNavigation={navigation}
            footerPages={footerPages}
            onRequestClose={() => this.setState({ activePageSettingsModal: null })}
          />
        )}
        {duplicatePageModal && (
          <PageModal
            mode="duplicate"
            page={duplicatePageModal}
            isActivePage={this.sfLocationPath === duplicatePageModal.get('href')}
            mainNavigation={navigation}
            footerPages={footerPages}
            onRequestClose={() => this.setState({ duplicatePageModal: null })}
          />
        )}
        {addPageModal && (
          <PageModal
            mode="create"
            page={Immutable.fromJS({
              type: 'blank',
              title: '',
              isVisible: true,
              navigation: 'main',
              selectedCollectionInfo: {
                isActive: false,
                collectionId: '',
              },
              pageParentSlug: '',
            })}
            mainNavigation={navigation}
            footerPages={footerPages}
            onRequestClose={() => this.setState({ addPageModal: false })}
          />
        )}
      </div>
    )
  }

  renderEditorialPages() {
    const { navigation } = this.props
    const editorialPages = navigation.skipWhile((i) => i.get('type') === 'category')
    const onDataChange = (pages) =>
      this.updateNavigation(navigation.takeWhile((i) => i.get('type') === 'category').concat(pages))
    const validate = this.validateNavigation

    return this.renderNestedList('editorial', editorialPages, onDataChange, {
      validate,
    })
  }

  renderCategories() {
    const { navigation } = this.props
    const categories = navigation.takeWhile((i) => i.get('type') === 'category')
    const onDataChange = noop
    const validate = noop

    return this.renderNestedList('categories', categories, onDataChange, {
      validate,
      isReadOnly: true,
    })
  }

  renderFooterPages() {
    const { footerPages = Immutable.List(), isBeyondShop } = this.props
    const data = footerPages.map((footerPage) => {
      const translatedTitle = isBeyondShop ? this.shopT(footerPage.get('title')) : footerPage.get('title')

      return footerPage.set('children', new Immutable.List()).set('title', translatedTitle)
    })
    const onDataChange = noop
    const validate = noop

    return this.renderNestedList('footer', data, onDataChange, {
      validate,
      isReadOnly: true,
    })
  }

  renderLegalPages() {
    const { legalPages = Immutable.List(), isBeyondShop } = this.props
    const data = legalPages.map((lp) => {
      const translatedTitle = isBeyondShop ? this.shopT(lp.get('title')) : lp.get('title')

      return lp.set('children', new Immutable.List()).set('title', translatedTitle)
    })
    const onDataChange = noop
    const validate = noop

    return this.renderNestedList('legal', data, onDataChange, {
      validate,
      isReadOnly: true,
    })
  }

  renderNestedList(id, data, onDataChange, { validate, isReadOnly }) {
    const getOlClassNames = (draggedIds) =>
      cc([
        'ep-simple-list',
        'ep-page-manager-list',
        `ep-page-manager-list-${id}`,
        {
          'ep-page-manager-list-preview': draggedIds,
        },
      ])
    const getLiClassNames = (item, draggedIds) =>
      cc([
        'ep-page-manager-list-item',
        {
          'ep-page-manager-list-item-draggable': !isReadOnly,
          'ep-page-manager-list-item-active': item.get('href') === this.sfLocationPath,
          'ep-page-manager-list-item-disabled':
            !item.get('isVisible') || (item.get('type') === 'category' && !item.get('visibleInNavigation')),
          'ep-page-manager-list-item-dragged': draggedIds && draggedIds.indexOf(item.get('id')) >= 0,
        },
      ])
    const itemHeight = 34
    const itemLeftPadding = 19

    return (
      <NestedList
        key={`${id}-list`}
        data={data}
        onDataChange={onDataChange}
        validate={validate}
        isReadOnly={isReadOnly}
      >
        {(items, draggedIds, validationError) =>
          items.size > 0 && (
            <ol
              ref={(node) => (this[`${id}-list`] = node)}
              className={getOlClassNames(draggedIds)}
              style={{ position: 'relative' }}
            >
              {flatMap(items, (item) => (
                <NestedListItem
                  key={`${item.get('id')}-item`}
                  item={item}
                  hasValidationError={Boolean(validationError)}
                  isDraggable={item.get('type') === 'blank'}
                >
                  <li
                    className={getLiClassNames(item, draggedIds)}
                    data-error-message={
                      validationError && draggedIds && draggedIds.indexOf(item.get('id')) >= 0 ? validationError : null
                    }
                    data-slug={item.get('slug')}
                    data-category-id={item.get('categoryId')}
                    data-level={item.get('__level')}
                    style={{ height: itemHeight }}
                  >
                    <div
                      className="ep-page-manager-list-item-wrapper"
                      style={{
                        position: 'absolute',
                        left: (item.get('__level') - 1) * itemLeftPadding,
                        right: 0,
                      }}
                    >
                      <span
                        title={item.get('title')}
                        className={
                          'ep-page-manager-content-type-' +
                          getContentTypeCategoryClassName(id, item.get('href'), item.get('type'))
                        }
                      />
                      <Link
                        className={cc([
                          'ep-page-manager-list-item-link',
                          {
                            'ep-page-manager-list-item-link-setting': item.get('type') !== 'blank',
                            'ep-page-manager-list-item-link-duplicate': item.get('type') === 'blank',
                          },
                        ])}
                        to={item.get('href')}
                        title={item.get('title')}
                        draggable={false}
                      >
                        {item.get('title')}
                      </Link>
                      {item.get('type') === 'blank' && (
                        <span
                          className="ep-page-manager-list-item-icon ep-page-manager-list-item-icon-duplicate"
                          title={this.tScoped('contentPagesSection.duplicatePageButton.label')}
                          onClick={() =>
                            this.setState({
                              duplicatePageModal: mapDeep([item], (p) =>
                                p
                                  .delete('__totalChildrenCount')
                                  .delete('__treeLines')
                                  .delete('__level')
                                  .delete('__parentId')
                                  .delete('__root')
                                  .delete('__indexGlobal')
                                  .delete('__indexLocal'),
                              )[0],
                            })
                          }
                        />
                      )}
                      <span
                        className="ep-page-manager-list-item-icon ep-page-manager-list-item-icon-setting"
                        title={this.settingsLabelForType(item.get('type'))}
                        onClick={() =>
                          this.setState({
                            activePageSettingsModal: mapDeep([item], (p) =>
                              p
                                .delete('__totalChildrenCount')
                                .delete('__treeLines')
                                .delete('__level')
                                .delete('__parentId')
                                .delete('__root')
                                .delete('__indexGlobal')
                                .delete('__indexLocal'),
                            )[0],
                          })
                        }
                      />
                    </div>
                  </li>
                </NestedListItem>
              )).concat(
                this.renderTreeLines(itemLeftPadding, itemHeight, items, {
                  style: {
                    position: 'absolute',
                    left: 18,
                    top: 0,
                    pointerEvents: 'none',
                  },
                }),
              )}
            </ol>
          )
        }
      </NestedList>
    )
  }

  renderTreeLines(segmentWidth, segmentHeight, items, props) {
    function pathData(type, left = 0, top = 0) {
      const [xMid, yMid] = [Math.round(segmentWidth / 2) - 0.5, Math.round(segmentHeight / 2) - 0.5]
      const base = ['M', left + xMid, top, 'L', left + xMid]
      switch (type) {
        case '│':
          return base.concat([top + segmentHeight])
        case '├':
          return base.concat([
            top + segmentHeight,
            'M',
            left + xMid,
            top + yMid,
            'L',
            left + segmentWidth - 5,
            top + yMid,
          ])
        case '└':
          return base.concat([top + yMid, 'L', left + segmentWidth - 5, top + yMid])
        default:
          return []
      }
    }

    // get flattened list of all items
    const itemsFlat = flatMap(items, (i) => i)

    // svg dimensions
    const width = segmentWidth * itemsFlat.reduce((max, item) => Math.max(max, item.get('__treeLines').count() - 2), 0)
    const height = segmentHeight * itemsFlat.count()

    // iterate over all items
    const d = itemsFlat.reduce((d, item, y) => {
      // iterate over all tree line segments for that item ('│', '├', '└' or ' ')
      // skip first two tree line segments layers to not have lines on root level
      return d.concat(
        item
          .get('__treeLines')
          .skip(2)
          .reduce((d2, type, x) => {
            // generate svg path data for that segment
            return d2.concat(pathData(type, x * segmentWidth, y * segmentHeight))
          }, []),
      )
    }, [])

    return (
      <svg
        key="svg-item"
        {...props}
        xmlns="http://www.w3.org/2000/svg"
        viewBox={[0, 0, width, height].join(' ')}
        width={width}
        height={height}
      >
        <path d={d.join(' ')} strokeWidth={1} stroke="rgba(255, 255, 255, 0.35)" fill="none" />
      </svg>
    )
  }

  settingsLabelForType(type) {
    return this.tScoped(
      {
        category: 'categories',
        blank: 'contentPages',
        legal: 'legalPages',
      }[type] + 'Section.pageSettingsButton.label',
    )
  }
}

PageManager.contextType = i18nInstancesContext

const mapStateToProps = (state) => ({
  isBeyondShop: Boolean(state.getIn(['shop', 'beyond'])),
  // Tutorial videos are only available for German merchants at the moment.
  isTutorialVideoAvailable: ['de'].includes(state.getIn(['view', 'interfaceLanguage'])),
})

const mapDispatchToProps = (dispatch) => ({
  updateNavigation: (newNavigation) => dispatch(storeUpdateNavigation(newNavigation)),
})

export default compose(withI18n('interface'), translate(), connect(mapStateToProps, mapDispatchToProps))(PageManager)
